import { useState } from 'react';
import { useUser } from '../../auth/AuthProvider';
import { documentsDueForReview, isManager, sgarCount, sgarStatus } from '../../data/domain';
import { useDocuments, useRodentPlans } from '../../data/hooks';
import type { ComplianceDocument } from '../../data/types';
import { daysUntil, displayDate, dateTime, plural } from '../../lib/format';
import { navigate } from '../../lib/router';
import { queueDocument } from '../../sync/outbox';
import { useUI } from '../../ui/UIProvider';
import { Icon } from '../../ui/Icon';
import { EmptyState, PageHeading, PanelHeader, View } from '../../ui/View';
import { downloadPlanImage, hasPlanImage } from '../rodent/planImage';
import { DocumentPreviewBody, downloadDocument, useDocumentFile } from './DocumentPreview';
import { DocumentUploadModal } from './DocumentUploadModal';

export function ComplianceView() {
  const user = useUser();
  const documents = useDocuments();
  const [selectedId, setSelectedId] = useState('');
  const [editing, setEditing] = useState<ComplianceDocument | 'new' | null>(null);
  const canManage = isManager(user);
  const selected = documents.find((doc) => doc.id === selectedId) ?? null;
  const due = documentsDueForReview(documents);

  async function remove(doc: ComplianceDocument) {
    if (!window.confirm(`Delete "${doc.documentName || doc.name}"? This cannot be undone.`)) return;
    await queueDocument('delete', doc, `Delete compliance document: ${doc.documentName || doc.name}`);
    if (selectedId === doc.id) setSelectedId('');
  }

  return (
    <View id="compliance">
      <PageHeading eyebrow="Compliance centre" title="SDS & chemical labels" subtitle="Maintain product documentation and monitor five-year SDS review dates.">
        {canManage && <button className="btn btn-primary" id="uploadDocument" onClick={() => setEditing('new')}>Upload document</button>}
      </PageHeading>
      <div className="stack">
        <div className="alert-list" id="reviewNotifications">
          {due.length ? due.map((doc) => {
            const days = daysUntil(new Date(`${doc.expiry}T00:00:00`));
            const status = days < 0 ? `${plural(Math.abs(days), 'day')} overdue` : days === 0 ? 'Review due today' : `Review due in ${plural(days, 'day')}`;
            return (
              <article className={`alert ${days < 0 ? 'alert-danger' : 'alert-warning'}`} key={doc.id}>
                <Icon name="alert" />
                <span className="row-text"><b>{doc.documentName || doc.name}</b><small>{doc.tag || 'Compliance document'} · {status}</small></span>
                {canManage ? <button className="btn btn-secondary btn-sm" onClick={() => setEditing(doc)}>Review</button> : <button className="btn btn-secondary btn-sm" onClick={() => setSelectedId(doc.id)}>View</button>}
              </article>
            );
          }) : <p className="alert alert-success"><Icon name="shield" /><span className="row-text"><b>No documents require review in the next 30 days.</b></span></p>}
        </div>
        <section className="panel">
          <PanelHeader title="Compliance library" summary="Select a document to preview it before downloading." />
          <div id="documentList" className="row-list">
            {documents.length ? documents.map((doc) => (
              <div key={doc.id} className={`row row-button document-row${selectedId === doc.id ? ' selected' : ''}`} tabIndex={0} role="button" aria-pressed={selectedId === doc.id}
                onClick={() => setSelectedId(doc.id)} onKeyDown={(event) => { if ((event.key === 'Enter' || event.key === ' ') && event.target === event.currentTarget) { event.preventDefault(); setSelectedId(doc.id); } }}>
                <span className="row-text">
                  <b>{doc.documentName || doc.name}</b>
                  <small>{doc.issuedDate ? `Issued ${displayDate(doc.issuedDate)} · ` : ''}{doc.expiry ? `Review ${displayDate(doc.expiry)}` : 'No review date'} · {doc.pending ? 'Waiting to sync' : `Uploaded ${dateTime(doc.uploadedAt)}`}</small>
                </span>
                <span className="badge badge-neutral">{doc.tag || 'Document'}</span>
                <span className="button-group" onClick={(event) => event.stopPropagation()}>
                  {canManage && <button className="btn btn-secondary btn-sm" data-edit-document onClick={() => setEditing(doc)}>Edit</button>}
                  <button className="btn btn-secondary btn-sm" onClick={() => downloadDocument(doc)}>Download</button>
                  {canManage && <button className="btn btn-danger btn-sm" data-delete-document onClick={() => remove(doc)}>Delete</button>}
                </span>
              </div>
            )) : <EmptyState>No uploaded compliance documents yet.</EmptyState>}
          </div>
        </section>
        {selected && <PreviewPanel document={selected} onClose={() => setSelectedId('')} />}
        <RodentPlanRegister />
      </div>
      {editing && <DocumentUploadModal document={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </View>
  );
}

function PreviewPanel({ document, onClose }: { document: ComplianceDocument; onClose(): void }) {
  const { url, status } = useDocumentFile(document);
  return (
    <section className="panel document-preview" id="documentPreview" aria-live="polite">
      <PanelHeader title={document.documentName || document.name} summary={`${document.tag || 'Compliance document'} · ${document.issuedDate ? `Issued/revised ${displayDate(document.issuedDate)} · ` : ''}${document.expiry ? `Review date ${displayDate(document.expiry)}` : 'No review date recorded'}`}>
        <button className="btn btn-secondary btn-sm" onClick={() => downloadDocument(document)}>Download</button>
        <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
      </PanelHeader>
      <div className="document-preview-body"><DocumentPreviewBody url={url} status={status} type={document.type} name={document.name} /></div>
    </section>
  );
}

function RodentPlanRegister() {
  const ui = useUI();
  const plans = useRodentPlans();
  const open = (planId: string) => { ui.open('rodentPlanRequest', { planId, at: Date.now() }); navigate('/rodent-planning'); };
  return (
    <section id="rodentCompliancePlans" className="panel">
      <PanelHeader title="Rodent plan register" summary="Saved station plans and SGAR site-review requirements"><button className="btn btn-secondary btn-sm" onClick={() => navigate('/rodent-planning')}>Open planner</button></PanelHeader>
      <div className="row-list">
        {plans.length ? plans.map((plan) => {
          const status = sgarStatus(plan);
          return (
            <article className="row plan-row" key={plan.id}>
              <span className="row-text"><b>{plan.clientName}</b><small>{plan.address}</small><small>{plural(plan.stations.length, 'station')} · {sgarCount(plan)} SGAR · Saved {displayDate(plan.savedAt) || 'date not recorded'}</small></span>
              <span className={`badge ${status.state === 'current' ? 'badge-success' : status.state === 'due' ? 'badge-warning' : 'badge-danger'}`}>{status.label}</span>
              <span className="button-group">
                {hasPlanImage(plan) && <button className="btn btn-secondary btn-sm" onClick={() => downloadPlanImage(plan)}>Download PNG</button>}
                <button className="btn btn-secondary btn-sm" onClick={() => open(plan.id)}>Open plan</button>
              </span>
            </article>
          );
        }) : <EmptyState>No rodent station plans have been saved yet.</EmptyState>}
      </div>
    </section>
  );
}
